import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { differenceInCalendarDays } from 'date-fns';
import { Flex, Button, Text } from '@radix-ui/themes';

import DatePicker from '../../../../shared/DatePicker/DatePicker.jsx';
import TextField from '../../../../shared/TextField/TextField.jsx';
import ToggleSwitch from '../../../../shared/FormStyles/ToggleSwitch.jsx';
import {
  useCreateCampaignMutation,
  useCopyCampaignMutation,
} from './hooks/useCampaignMutation';
import { MAX_CAMPAIGN_DURATION_DAYS } from './constants';
import styles from './HighlightForm.module.css';

export default function HighlightForm({
  onSave,
  onCancel,
  campaignName = '',
  campaignUuid = null,
  isNewHire = false,
  isMultiMonth = false,
}) {
  const queryClient = useQueryClient();
  const { createCampaignMutation } = useCreateCampaignMutation();
  const { copyCampaignMutation } = useCopyCampaignMutation();
  const { t } = useTranslation('campaigns');

  const [name, setName] = useState(
    campaignName ? `${campaignName} (Copy)` : '',
  );
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [newHireValue, setNewHireValue] = useState(isNewHire);
  const [error, setError] = useState('');

  const handleSave = () => {
    // Validate name
    if (!name.trim()) {
      setError(t('highlightForm.nameRequired'));
      return;
    }

    // Validate dates
    if (!startDate) {
      setError(t('simpleDateForm.startDateRequired'));
      return;
    }

    if (!endDate) {
      setError(t('simpleDateForm.endDateRequired'));
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError(t('simpleDateForm.startDateAfterEndDate'));
      return;
    }

    if (
      !isMultiMonth &&
      differenceInCalendarDays(new Date(endDate), new Date(startDate)) >
        MAX_CAMPAIGN_DURATION_DAYS
    ) {
      setError(
        t('highlightForm.maxDuration', { maxDays: MAX_CAMPAIGN_DURATION_DAYS }),
      );
      return;
    }

    setError('');

    const isDuplicate = campaignUuid !== null;

    const campaignData = {
      name: name.trim(),
      start_date: startDate,
      end_date: endDate,
      ...(isDuplicate ? {} : { new_hire: newHireValue }),
    };

    const mutationOptions = {
      onSuccess: () => {
        // Invalidate queries to refresh the list
        queryClient.invalidateQueries(['campaigns']);
        queryClient.invalidateQueries(['campaigns-by-year']);

        // Call the original onSave for any additional cleanup
        onSave({
          name: name.trim(),
          dates: [startDate, endDate],
        });
      },
      onError: mutationError => {
        const { response, message } = mutationError || {};
        const { data } = response || {};

        if (data?.errors) {
          const errorMessages = Array.isArray(data.errors)
            ? data.errors
            : Object.values(data.errors).flat();
          setError(errorMessages.join(', '));
          return;
        }

        if (data?.message) {
          setError(data.message);
          return;
        }

        setError(message);
      },
    };

    // Use different mutation based on whether this is a duplicate or new campaign
    if (campaignUuid) {
      copyCampaignMutation(
        {
          uuid: campaignUuid,
          campaignData,
        },
        mutationOptions,
      );
    } else {
      createCampaignMutation(
        {
          campaign: campaignData,
        },
        mutationOptions,
      );
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleDateChange = dates => {
    if (dates && dates.length > 0) {
      setStartDate(dates[0]);
      setEndDate(dates[1] || '');
      setError('');
    }
  };

  const handleNameChange = e => {
    setName(e.target.value);
    if (error && e.target.value.trim()) {
      setError('');
    }
  };

  return (
    <Flex direction="column" gap="3">
      <TextField
        name="highlight-name"
        label={t('highlightForm.highlightName')}
        placeholder={t('highlightForm.namePlaceholder')}
        value={name}
        onChange={handleNameChange}
        error={error && !startDate && !endDate ? error : ''}
      />

      <ToggleSwitch
        id="new-hire-toggle"
        name="newHire"
        value={newHireValue}
        onChange={e => setNewHireValue(e.target.checked)}
        label={t('highlightForm.newHireLabel')}
        margin="0"
        disabled={campaignUuid !== null}
      />

      <div className={styles.calendarWrapper}>
        <DatePicker
          value={[startDate, endDate]}
          onChange={handleDateChange}
          minDate="today"
          mode="range"
          size={1}
        />
      </div>

      {error && (startDate || endDate) && (
        <Text size="2" className={styles.errorMessage}>
          {error}
        </Text>
      )}

      <Flex gap="3" justify="end">
        <Button variant="outline" size="3" onClick={handleCancel}>
          {t('highlightForm.cancel')}
        </Button>
        <Button variant="solid" size="3" onClick={handleSave}>
          {t('highlightForm.addHighlight')}
        </Button>
      </Flex>
    </Flex>
  );
}
